import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import Form from '../../../../../Components/Form/Form';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../Components/Form/withForm';
import FormControl from '../../../../../Components/Form/FormControl';
import TimePicker from '../../../../../Components/Base/TimePicker/TimePicker';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import moment, { Moment } from 'moment-timezone';
import { getDate } from '../../../../../Helpers/date.helper';

//@ts-ignore
import styles from './ElectronicVaccinationCreateForm.module.scss';
import NCZIReferenceSelectField from '../../ReferenceSelect/NCZIReferenceSelectField';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import InputField from '../../../../../Components/Form/components/InputField';
import FormContainer from '../../../../../Components/Form/FormContainer';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import NCZIExtensionSearchSelectField from '../../ExtensionSearchSelect/NCZIExtensionSearchSelectField';
import {
	NCZIReferenceSelectItem,
	NCZIReferenceValidator,
} from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import {
	NCZIExtensionSelectItem,
	NCZIExtensionValidator,
} from '../../ExtensionSearchSelect/NCZIExtensionSearchSelect';
import { WritePatientVaccinationResult } from '../../../Commands/WritePatientVaccinationResult';
import TextAreaField from '../../../../../Components/Form/components/TextAreaField';

export interface ElectronicVaccinationCreateFormValues {
	vaccinationDate: Moment;
	vaccinationTime: Moment;
	vaccinationKind: NCZIReferenceSelectItem;
	vaccinationType: NCZIReferenceSelectItem;
	dose: {
		sequence: number;
		totalCount: number;
		isLast: boolean;
	};
	note: string;
	vaccine: {
		batch: string;
		amount: {
			amount: string;
			unit: NCZIReferenceSelectItem;
		};
		substance: NCZIExtensionSelectItem;
		note: string;
	};
}

interface Props {
	onClose?: (needReload: boolean) => void;
	patientIdentifier: string;
}

const ElectronicVaccinationCreateForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier } = props;
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});

	const handleSubmit = async (
		values: ElectronicVaccinationCreateFormValues,
	) => {
		const vaccinationTime = values.vaccinationDate;
		vaccinationTime.set({
			hour: values.vaccinationTime.get('hour'),
			minute: values.vaccinationTime.get('minute'),
			second: values.vaccinationTime.get('second'),
		});

		const finalValues = {
			createdAt: moment().format(),
			versionStatus: 'VER01',
			sensitivity: 3,
			uncertaintyExpressed: false,
			kind: values.vaccinationKind.value,
			type: values.vaccinationType.value,
			note: values.note || undefined,
			dose: values.dose,
			executed: {
				dateTime: vaccinationTime.format(),
				vaccine: {
					substance: values.vaccine.substance.value,
					note: values.vaccine.note || undefined,
					batch: values.vaccine.batch,
					amount: {
						amount: values.vaccine.amount.amount,
						unit: values.vaccine.amount.unit.value,
					},
				},
			},
		};

		const writeCommand = WritePatientVaccinationResult({
			patientCode: patientIdentifier,
			vaccination: finalValues,
		});

		await processCommand(writeCommand);
	};

	const validationSchema = yup.object({
		vaccinationKind: NCZIReferenceValidator(
			'nczi.vaccination.create.vaccinationKind',
		),
		vaccinationType: NCZIReferenceValidator(
			'nczi.vaccination.create.vaccinationType',
		),
		dose: yup.object({
			sequence: yup.number().required('nczi.vaccination.create.dose.sequence'),
			totalCount: yup
				.number()
				.required('nczi.vaccination.create.dose.totalCount'),
		}),
		note: yup
			.string()
			.max(2000, 'nczi.validation.max2000'),
		vaccine: yup.object({
			batch: yup.string()
				.max(2000, 'nczi.validation.max2000')
				.required('nczi.vaccination.create.vaccine.batch'),
			amount: yup.object({
				amount: yup
					.string()
					.required('nczi.vaccination.create.vaccine.amount.amount'),
				unit: NCZIReferenceValidator(
					'nczi.vaccination.create.vaccine.amount.unit',
				),
			}),
			substance: NCZIExtensionValidator(
				'nczi.vaccination.create.vaccine.substance',
			),
			note: yup.string(),
		}),
	});

	useEffect(() => {
		const now = getDate();
		setDefaultValues({
			vaccinationDate: now,
			vaccinationTime: now,
		});
		setIsLoading(false);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<div>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					<div className={'row'}>
						<div className={'col-6'}>
							<FormControl
								label='nczi.vaccination.create.vaccinationDate'
								name='vaccinationDate'
								isRequired>
								<DatePicker />
							</FormControl>
						</div>
						<div className={'col-6'}>
							<FormControl
								label='nczi.vaccination.create.vaccinationTime'
								name='vaccinationTime'
								isRequired>
								<TimePicker />
							</FormControl>
						</div>
					</div>
					<div className={'row'}>
						<div className={'col-6'}>
							<InputWrapper>
								<NCZIReferenceSelectField
									label='nczi.vaccination.create.vaccinationKind'
									name='vaccinationKind'
									oid={NCZI_OID.VACCINATION_KIND}
									isRequired
								/>
							</InputWrapper>
						</div>
						<div className={'col-6'}>
							<InputWrapper>
								<NCZIReferenceSelectField
									label='nczi.vaccination.create.vaccinationType'
									name='vaccinationType'
									oid={NCZI_OID.VACCINATION_TYPE}
									isRequired
								/>
							</InputWrapper>
						</div>
					</div>
					<InputWrapper>
						<TextAreaField
							label='nczi.vaccination.create.note'
							name='note'
							isRequired={false}
							charactersLimit={2000}
						/>
					</InputWrapper>
					<FormContainer name={'dose'} settings={{ showBox: true }}>
						<div className={'row'}>
							<div className={'col-6'}>
								<InputWrapper>
									<InputField
										label='nczi.vaccination.create.dose.sequence'
										name='sequence'
										isRequired
									/>
								</InputWrapper>
							</div>
							<div className={'col-6'}>
								<InputWrapper>
									<InputField
										label='nczi.vaccination.create.dose.totalCount'
										name='totalCount'
										isRequired
									/>
								</InputWrapper>
							</div>
						</div>
						<CheckboxField
							tx='nczi.vaccination.create.dose.isLast'
							name='isLast'
							isRequired
							firstUpper
						/>
					</FormContainer>
					<FormContainer name={'vaccine'} settings={{ showBox: true }}>
						<InputWrapper>
							<NCZIExtensionSearchSelectField
								name='substance'
								oid={NCZI_OID.MEDICAMENTS}
								label='nczi.vaccination.create.vaccine.substance'
								isRequired
								onlyVaccination
								showAdditionalName
							/>
						</InputWrapper>
						<InputWrapper>
							<InputField
								label='nczi.vaccination.create.vaccine.note'
								name='note'
								isRequired={false}
								charactersLimit={2000}
							/>
						</InputWrapper>
						<InputWrapper>
							<InputField
								label='nczi.vaccination.create.vaccine.batch'
								name='batch'
								isRequired
								charactersLimit={2000}
							/>
						</InputWrapper>
						<FormContainer name={'amount'}>
							<div className={'row'}>
								<div className={'col-6'}>
									<InputWrapper>
										<InputField
											label='nczi.vaccination.create.vaccine.amount.amount'
											name='amount'
											isRequired
										/>
									</InputWrapper>
								</div>
								<div className={'col-6'}>
									<InputWrapper>
										<NCZIReferenceSelectField
											label='nczi.vaccination.create.vaccine.amount.unit'
											name='unit'
											oid={NCZI_OID.UNIT}
											isRequired
										/>
									</InputWrapper>
								</div>
							</div>
						</FormContainer>
					</FormContainer>
					<div className='mt-4'>
						<SubmitButton
							title='component.nczi.setup.save'
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			)}
		</div>
	);
};

export default withForm(ElectronicVaccinationCreateForm);
